import { Input, Textarea as FieldTextarea } from '@components/Field'
import styled from 'styled-components'
import {
	orange,
	blue,
	black,
	textMuted,
	mutedGrey,
	lightGrey,
	white,
	grey,
	surface2,
	border1,
} from '@/theme/colors'
import { shadows, radii, motion } from '@/theme/tokens'
import { fontSizes, leading } from '@/theme/typography'
import { dashedSlot } from '@/theme/recipes'
import { Button } from '@components/Button'

export const Container = styled.div`
	display: flex;
	flex-direction: column;
	gap: 20px;
`

/** The title edits in place: flat until hovered or focused. */
export const TitleInput = styled.input`
	width: calc(100% + 20px);
	margin-left: -10px;
	margin-right: -10px;
	font-size: ${fontSizes.title};
	font-weight: 700;
	color: ${black};
	border: 1px solid transparent;
	border-radius: ${radii.small};
	padding: 8px 10px;
	background: transparent;
	transition:
		background-color ${motion.fast},
		border-color ${motion.fast},
		box-shadow ${motion.fast};

	&:hover {
		background: ${surface2};
	}

	&:focus {
		outline: none;
		border-color: ${orange};
		background: ${white};
		box-shadow: ${shadows.focus};
	}
`

export const FieldRow = styled.div`
	display: flex;
	flex-direction: column;
	gap: 8px;
`

export const FieldHeader = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
`

export const FieldLabel = styled.label`
	font-size: ${fontSizes.caption};
	font-weight: 500;
	color: ${textMuted};
	display: inline-flex;
	align-items: center;
	gap: 6px;

	svg {
		width: 14px;
		height: 14px;
		fill: currentColor;
	}
`

export const EditButton = styled(Button).attrs({ variant: 'ghost' as const, size: 'sm' as const })``

export const FieldValue = styled.div`
	font-size: ${fontSizes.small};
	color: ${black};
	line-height: ${leading.body};
	white-space: pre-wrap;
	word-break: break-word;
	padding: 8px 8px;
	margin: 0 -8px;
	border-radius: ${radii.small};
	background: transparent;
`

export const EmptyValue = styled(FieldValue)`
	color: ${mutedGrey};
	font-style: italic;
`

export const Textarea = styled(FieldTextarea)`
	min-height: 90px;
`

export const ButtonRow = styled.div`
	display: flex;
	gap: 8px;
	justify-content: flex-end;
`

export const PrimaryButton = styled(Button).attrs({ size: 'sm' as const })``

export const SecondaryButton = styled(Button).attrs({ variant: 'secondary' as const, size: 'sm' as const })``

export const LinkInputWrapper = styled.div`
	display: flex;
	gap: 6px;
	margin-bottom: 6px;
`

export const LinkTextInput = styled(Input)`
	flex: 1;
`

export const RemoveLinkBtn = styled(Button).attrs({ variant: 'danger' as const, size: 'sm' as const })`
	padding: 0 10px;
`

export const AddLinkBtn = styled.button`
	${dashedSlot}
`

export const LinkListReadOnly = styled.div`
	display: flex;
	flex-direction: column;
	gap: 8px;
`

export const LinkPreview = styled.a`
	display: flex;
	align-items: center;
	gap: 10px;
	padding: 8px;
	border: 1px solid ${border1};
	border-radius: ${radii.small};
	background: ${surface2};
	text-decoration: none;
	transition:
		background-color ${motion.fast},
		border-color ${motion.fast};
	overflow: hidden;
	min-width: 0;

	&:hover {
		background: ${grey};
		border-color: ${orange};
	}
`

export const StaticLinkPreview = styled(LinkPreview)`
	cursor: default;
`

export const LinkThumbnail = styled.div<{ $src?: string }>`
	width: 96px;
	height: 72px;
	min-width: 96px;
	border-radius: ${radii.small};
	background: ${props => (props.$src ? `url(${props.$src})` : lightGrey)};
	background-size: cover;
	background-position: center;
	display: flex;
	align-items: center;
	justify-content: center;
	overflow: hidden;
	flex-shrink: 0;
`

export const LinkInfo = styled.div`
	flex: 1;
	min-width: 0;
	display: flex;
	flex-direction: column;
	gap: 2px;
`

export const LinkSource = styled.div<{ $color?: string }>`
	font-size: ${fontSizes.micro};
	font-weight: 700;
	color: ${({ $color }) => $color ?? textMuted};
	text-transform: capitalize;
`

export const LinkUrl = styled.div`
	font-size: ${fontSizes.micro};
	color: ${blue};
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
`

export const LocationDisplay = styled.a`
	display: inline-flex;
	align-items: center;
	gap: 6px;
	color: ${orange};
	text-decoration: none;
	font-size: ${fontSizes.small};
	padding: 8px 8px;
	margin: 0 -8px;
	width: fit-content;

	&:hover {
		text-decoration: underline;
	}
`

export const LocationSecondary = styled.div`
	font-size: ${fontSizes.micro};
	color: ${textMuted};
	margin-top: 4px;
`
